<template>
  <v-card class="glass-background">

    <!-- FLOATING HEADER (darf über den Rand gehen) -->
    <div class="header-floating">
      <v-row dense class="justify-start">
        <v-col cols="auto">
          <Headers
            :headersTitle="'Reservierungen'"
            :headersIcon="'mdi-clipboard-check'"
          />
        </v-col>
      </v-row>
    </div>

    <!-- HAUPT-INHALT → echter flex container -->
    <div class="content">
      <div class="filters">
        <v-row dense class="justify-center align-center">
          <v-col cols="auto">
            <DatePremisesMenu 
              v-model="selectedDateRange"
              v-model:modelPremise="selectedPremise"
              :datePremisesMenuPremises ="reservationCardPremises"/>
          </v-col>
        </v-row>
      </div>

      <!-- MITTLERER FLEXBEREICH -->
      <div class="reservations-area">
        <v-card class="reservationsCard">
            <div class="reservations-wrapper">
              <template v-if="reservationCardFiltered.length === 0">
                <v-card class="cardNoRes">
                  <div>Keine Reservierungen vorhanden.</div>
                </v-card>
              </template>

              <template v-else>
                <div
                  v-for="reservation in reservationCardFiltered"
                  :key="reservation.id"
                >
                  <ReservationCard
                    :resCardReservation="reservation"
                    :resCardUsers="reservationCardUsers"
                    :resCardPremises="reservationCardPremises"
                    :resCardStartDate="getMinDate(reservation.reservation_items)"
                    :resCardEndDate="getMaxDate(reservation.reservation_items)"
                    :resCardSelected="isSelected(reservation)"
                    @toggle="toggleReservation"
                    @updateReservation="$emit('updateReservation')"
                    @updateFavorite="$emit('updateFavorite')"
                    @updateOccupancy="$emit('updateOccupancy', reservation.seat)"
                  />
                </div>
              </template>
            </div>
        </v-card>
      </div>

      <!-- FOOTER → Immer unten -->
      <div class="footer">
        <v-card class="reservationButtonsCard" elevation="12">
          <v-row dense class="align-center">
            <v-col cols="auto">
              <v-btn 
                prepend-icon="mdi-checkbox-marked-circle" 
                variant="elevated"
                color="grey"
                :disabled="reservationCardReservations === null" 
                @click="selectAll"
                class="mr-4">
                Alle markieren
              </v-btn>
              <v-btn 
                prepend-icon="mdi-checkbox-marked-circle-outline" 
                variant="elevated"
                color="grey"
                :disabled="selectedReservations.length === 0" 
                @click="clearSelection">
                Auswahl entfernen
              </v-btn>
            </v-col>
            <v-col cols="auto" class="ml-auto">
              <v-btn 
                prepend-icon="mdi-cancel" 
                variant="elevated" 
                color="red" 
                :disabled="selectedReservations.length === 0"
                @click="showDialogStorno">
                Ausgewählte stornieren
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </div>
    </div>
  </v-card>

  <ReservationStornoDialog
    ref="resStornoDialogRef"
    :resStornoDialogReservations="selectedReservations"
    @stornoReservation="handleStorno"
  />

</template>

<script>

import Headers from '../consumables/Headers.vue'
import DatePremisesMenu from './DatePremisesMenu.vue'
import DropDownFavorites from '../consumables/DropDownFavRes.vue'
import ReservationCard from '../components/ReservationCard.vue'
import ReservationStornoDialog from './ReservationStornoDialog.vue'

export default {
  components: {
    Headers,
    DatePremisesMenu,
    DropDownFavorites,
    ReservationCard,
    ReservationStornoDialog
  },

  props: {
    reservationCardReservations: {type: Object, default: null},
    reservationCardUsers: {type: Object, default: null},
    reservationCardPremises: {type: Object, default: null}
  },

  emits: ['updateReservation', 'updateFavorite', 'updateOccupancy'],

  data() {
    return {
      selectedPremise: '',
      selectedOption: '',
      selectedDateRange: {start: '', end: ''},
      selectedReservations: []
    }
  },

  watch: {
    selectedDateRange: {
      handler() {
        this.reservationCardFiltered
      },
      deep: true
    },
    selectedPremise: {
      handler(){
        this.reservationCardFiltered
      }
    }
  },

  computed: {
    reservationCardFiltered() {

      if (!this.reservationCardReservations?.length) return []

      let filteredReservations = []

      if (this.selectedDateRange.start && this.selectedDateRange.end) {
        filteredReservations = this.reservationCardReservations.filter(reservation => {
          return reservation.reservation_items.some(item => {
            return (
              item.start <= this.selectedDateRange.end &&
              item.end >= this.selectedDateRange.start
            )
          })
        })
      } else {
        filteredReservations = this.reservationCardReservations
      }

      if (this.selectedPremise === 'Alle' || this.selectedPremise === '') {
        return filteredReservations
      }

      return filteredReservations.filter(
        reservation => reservation.seat.premises === this.selectedPremise
      )
    }
  },

  methods: {
    isSelected(reservation) {
      return this.selectedReservations.some(r => r.id === reservation.id)
    },

    toggleReservation(reservation) {
      const exists = this.selectedReservations.some(r => r.id === reservation.id);

      if (exists) {
        this.selectedReservations = this.selectedReservations.filter(r => r.id !== reservation.id);
      } else {
        this.selectedReservations.push(reservation);
      }
    },

    selectAll() {
      this.selectedReservations = [...this.reservationCardFiltered]
    },

    clearSelection() {
      this.selectedReservations = []
    },

    showDialogStorno() {
      this.$refs.resStornoDialogRef.openDialogResStorno(this.selectedReservations)
    },

    handleStorno() {
      this.$emit('updateReservation')
      this.$emit('updateOccupancy', null)
      this.selectedReservations = []
    },

    formatDate(iso) {
      const date = new Date(iso)
      return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`
    },

    getMinDate(items) {
      if (!items || items.length === 0) return ''
      const sorted = [...items].sort((a, b) => new Date(a.start) - new Date(b.start))
      return this.formatDate(sorted[0].start)
    },

    getMaxDate(items) {
      if (!items || items.length === 0) return ''
      const sorted = [...items].sort((a, b) => new Date(b.end) - new Date(a.end))
      return this.formatDate(sorted[0].end)
    },
  },
}
</script>

<style scoped>

.glass-background {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 47.75rem;
  padding: 1rem;
  position: relative;         
  overflow: visible !important;    
  background: rgba(var(--v-theme-surface), .72);
  backdrop-filter: blur(8px) saturate(120%);
  border: 1px solid rgba(var(--v-theme-surface),.35);
  border-radius: 32px;
}
.header-floating {
  position: relative;
  margin-top: -2rem;
  margin-left: 0.5rem;     
  z-index: 10;
  flex-shrink: 0;
}
.content {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;         
}
.filters {
  flex-shrink: 0;
  margin-top: 0.5rem;
}
.reservations-area {
  margin-top: 1rem;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.reservationsCard {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
}
.reservations-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}
.footer {
  flex-shrink: 0;
  margin-top: 0.5rem;
}
.reservationButtonsCard {
  border-radius: 16px;
  padding: 0.7rem 1rem;
  width: 100%;
}
.cardNoRes {
  border-radius: 8px;
  font-size: larger;
  font-weight: bold;
  width: 100%;
  justify-content: center; 
  align-items: center;     
  height: 100%;           
  text-align: center;
  padding: 2rem;          
}
</style>

